import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [verifiedData, setVerifiedData] = useState(null);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setLoading(false);
      setError(t('verifyEmail.failed'));
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { token });
      setSuccess(true);
      setVerifiedData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('verifyEmail.failed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <CardTitle>{t('verifyEmail.verifying')}</CardTitle>
            <CardDescription>
              {t('verifyEmail.pleaseWait')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle>{t('verifyEmail.success')}</CardTitle>
            <CardDescription>
              {t('verifyEmail.fullAccess')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {verifiedData && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg space-y-2">
                <p className="text-sm font-medium text-green-800">{t('verifyEmail.verifiedDetails')}</p>
                <p className="text-sm text-green-700">
                  <span className="font-medium">{t('common.email')}:</span> {verifiedData.email}
                </p>
                <p className="text-sm text-green-700">
                  <span className="font-medium">{t('common.name')}:</span> {verifiedData.name}
                </p>
                <p className="text-sm text-green-700">
                  <span className="font-medium">{t('auth.operatorId')}:</span> {verifiedData.operator_id}
                </p>
              </div>
            )}
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground text-center">
                {t('verifyEmail.trialStarted')}
              </p>
            </div>
            <Button className="w-full" onClick={() => navigate('/login')}>
              {t('verifyEmail.loginToDashboard')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <XCircle className="w-8 h-8 text-destructive" />
          </div>
          <CardTitle>{t('verifyEmail.verificationFailed')}</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {t('verifyEmail.linkExpired')}
          </p>
          <div className="flex flex-col gap-2">
            <Button asChild className="w-full">
              <Link to="/login">{t('verifyEmail.goToLogin')}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
